'use client'

import { useMemo, useState } from 'react'
import { ContentfulLivePreview } from '@contentful/live-preview'
import { ariaSpeak } from '@cerifi/utils/aria-speak'

import { Container } from '../../components/container'
import { ProductCard } from '../../components/product-card/product-card'
import { useBreakpoints } from '../../hooks/use-breakpoints'
import { CmsColor } from '../../models/cms-color'
import { cn } from '../../utils/cn'
import { AuthorCardCms } from '../author-card/author-card.cms'
import { BrandCardCMS, BrandCardProps } from '../brand-card/brand-card.cms'
import { ButtonCms, ButtonCmsModel } from '../button/button.cms'
import { ArticleCardCms } from '../cards/article-card-cms'
import { ImageCardCms } from '../cards/image-card-cms'
import { LogoCardCms } from '../cards/logo-card-cms'
import TestimonialCardCms from '../cards/testimonial-card-cms'
import TextCardCms from '../cards/text-card-cms'
import { BaseCMSGrid } from '../helper-components/base-grid'
import { BaseResizingCMSSlider } from '../helper-components/resizing-slider'
import { RichText } from '../helper-components/rich-text'
import {
  BackgroundShapeType,
  CmsBackgroundShape,
} from '../image-shape/background-shape'
import styles from './layout-list.module.css'

const DEFAULT_NUMBER_OF_COLUMNS = 3
const VIEW_ALL_TERM = 'View All'
interface LayoutListCmsProps {
  id: string
  isH1?: boolean
  type?: 'grid' | 'slider'
  title?: string | any
  subtitle?: string | any
  collection: Array<any>
  numberOfColumns?: number
  interval?: number
  color?: CmsColor
  backgroundShape?: BackgroundShapeType
  paddingOnTop?: boolean
  paddingOnBottom?: boolean
  transparentCardBackground?: boolean
  textAlignment: 'Center' | 'Left'
  button?: ButtonCmsModel
  cardBorder?: boolean
  overlapPreviousComponent?: boolean
  cardColor?: CmsColor
  stackOnMobile?: boolean
}

export const LayoutListCms = (props: LayoutListCmsProps) => {
  const breakpoints = useBreakpoints()

  // Overlap only applied for: desktop size - one row of items (4 or fewer), no title & description, image cards only
  let showOverlap =
    props.overlapPreviousComponent &&
    !props.title &&
    !props.subtitle &&
    props.collection.length <= 4 &&
    props.collection.length <=
      (props.numberOfColumns ?? DEFAULT_NUMBER_OF_COLUMNS) &&
    props.type === 'grid' &&
    breakpoints.isDesktop

  // Overlap previous component for Image Cards only
  if (showOverlap) {
    props.collection.forEach((card) => {
      if (card.__typename !== 'CardImage') {
        showOverlap = false
      }
    })
  }

  /** Only used by brand cards to allow filtering by Brand Vertical (brand category) */
  const chipsInit = () => {
    const brands = new Set<string>([VIEW_ALL_TERM])
    props.collection.forEach((card) => {
      if (card.__typename === 'Brand') {
        const brand = card as BrandCardProps
        brands.add(brand.brandVertical.title)
      }
    })
    if (brands.size > 1) {
      return Array.from(brands)
    } else {
      return []
    }
  }

  const [filterChips] = useState<string[]>(chipsInit())
  const [selectedFilter, setSelectedFilter] = useState(new Set([VIEW_ALL_TERM]))

  /** Brand Lists cannot be sliders, they are always grids.
   *  If the user adds even a single brand to the
   *  list, it will automatically become a grid.
   */
  const [forceGrid] = useState(
    props.collection.every((card) => card.__typename === 'Brand')
  )

  function renderCard(card: any) {
    switch (card.__typename) {
      case 'CardTestimonial':
        return (
          <TestimonialCardCms
            card={card}
            cardBorder={props.cardBorder}
            alignment={props.textAlignment}
          />
        )
      case 'CardText':
        return (
          <TextCardCms
            card={card}
            cardBorder={props.cardBorder}
            alignment={props.textAlignment}
          />
        )
      case 'CardImage':
        return (
          <ImageCardCms
            card={card}
            cardBorder={props.cardBorder}
            textAlignment={props.textAlignment}
            showOverlap={showOverlap}
          />
        )
      case 'ArticlePage':
        return (
          <ArticleCardCms
            card={card}
            type="list"
            isFeatured={true}
            alignment={props.textAlignment}
            color={props.cardColor}
          />
        )
      case 'CardLogo':
        return <LogoCardCms card={card} />
      case 'Author':
        return <AuthorCardCms card={card} />
      case 'Product':
        return <ProductCard {...card} />
      case 'Brand':
        return <BrandCardCMS card={card} />
      default:
        console.warn('unknown card type: ' + card.__typename)
        return null
    }
  }

  const onFilter = (term: string) => {
    ariaSpeak(`filtering list`)

    const copy = new Set(selectedFilter)

    if (term === VIEW_ALL_TERM) {
      setSelectedFilter(new Set([VIEW_ALL_TERM]))
      return
    }

    if (copy.has(term)) {
      copy.delete(term)
    } else {
      copy.add(term)
    }

    if (copy.size === 0) {
      copy.add(VIEW_ALL_TERM)
    }
    if (copy.size > 1 && copy.has(VIEW_ALL_TERM)) {
      copy.delete(VIEW_ALL_TERM)
    }

    setSelectedFilter(copy)
  }

  const renderCards = () => {
    const collection =
      selectedFilter.has(VIEW_ALL_TERM) === false
        ? props.collection.filter((x) =>
            selectedFilter.has(x.brandVertical?.title)
          )
        : props.collection

    return collection.map((item, i) => (
      <div key={i} className="flex h-full justify-center items-center">
        <div
          className={cn(
            'h-full w-full max-w-[53.6rem]',
            !props.transparentCardBackground ? 'bg-white' : 'border-none',
            showOverlap && 'lg:bg-white',
            props.textAlignment === 'Left' ? 'text-left' : 'text-center'
          )}
          style={{
            color:
              props.transparentCardBackground && !showOverlap
                ? props.color?.textColor
                : '',
          }}
        >
          {renderCard(item)}
        </div>
      </div>
    ))
  }

  const type = useMemo(() => {
    if (forceGrid || (breakpoints.isMobile && props.stackOnMobile)) {
      return 'grid'
    }

    if ((props.type === 'grid' || !props.type) && !breakpoints.isMobile) {
      return 'grid'
    }

    return 'slider'
  }, [forceGrid, breakpoints.isMobile, props.stackOnMobile, props.type])

  return (
    <div id={`${props.id}`} className="scroll-mt-25">
      <div
        className={cn(
          'relative overflow-hidden',
          props.paddingOnTop ? 'pt-6 lg:pt-12' : '',
          props.paddingOnBottom ? 'pb-6 lg:pb-12' : '',
          showOverlap && styles.clearfix,
          showOverlap && 'overflow-visible'
        )}
        style={{
          backgroundColor: props.color?.backgroundColor || '',
        }}
      >
        {props.backgroundShape?.shapeName && !showOverlap && (
          <CmsBackgroundShape
            content={props.backgroundShape as any}
            color={props.color?.backgroundColor || 'slategray'}
          />
        )}

        <div className={cn('z-2 relative', showOverlap && 'lg:mt-[-60px]')}>
          <Container>
            <div
              className={cn(
                props.textAlignment === 'Left' ? 'text-left' : 'text-center'
              )}
            >
              {props.title && (
                <h2 className="font-sans">
                  <RichText
                    json={props.title.json}
                    links={props.title.links}
                    data-testid="layout-list-title"
                    className={cn(
                      forceGrid
                        ? 'text-3xl lg:text-4xl font-medium lg:leading-[2.925rem] tracking-[0.04875rem] pt-6 pb-4 lg:py-8 text-left'
                        : 'font-serif text-4xl leading-[2.925rem] sm:text-5xl sm:leading-[4.5rem] py-8'
                    )}
                    style={{ color: props.color?.textColor || '' }}
                    {...ContentfulLivePreview.getProps({
                      entryId: props.id,
                      fieldId: 'title',
                      locale: 'en-US',
                    })}
                  />
                </h2>
              )}
              {props.subtitle && (
                <h3>
                  <RichText
                    {...ContentfulLivePreview.getProps({
                      entryId: props.id,
                      fieldId: 'subtitle',
                      locale: 'en-US',
                    })}
                    className="-mt-3 pb-[1rem] text-base font-sans leading-[1.8rem] sm:text-2xl sm:leading-[2.4rem] md:pb-8"
                    data-testid="layout-list-subtitle"
                    json={props.subtitle?.json}
                    links={props.subtitle?.links}
                    style={{ color: props.color?.textColor || '' }}
                  />
                </h3>
              )}
            </div>
          </Container>

          {filterChips.length > 0 && !showOverlap && (
            <Container className="overflow-x-auto w-full">
              <ul
                className="flex gap-2 whitespace-nowrap mt-2 lg:mb-10 lg:mt-1 pb-[1rem]"
                aria-label="Filter List of Brands"
              >
                {filterChips.map((chip) => (
                  <li key={chip}>
                    <button
                      onClick={() => onFilter(chip)}
                      aria-pressed={selectedFilter.has(chip)}
                      className={cn(
                        'h-[2.25rem] px-4  font-semibold rounded-full flex flex-col justify-center text-sm',
                        selectedFilter.has(chip)
                          ? 'bg-lightBlue-100 border border-transparent hover:bg-lightBlue-300'
                          : 'border border-neutral-300 text-navy hover:bg-neutral-200'
                      )}
                    >
                      {chip}
                    </button>
                  </li>
                ))}
              </ul>
            </Container>
          )}

          {type === 'grid' ? (
            <>
              <BaseCMSGrid
                {...ContentfulLivePreview.getProps({
                  entryId: props.id,
                  fieldId: 'content',
                  locale: 'en-US',
                })}
                numberOfColumns={
                  props.numberOfColumns || DEFAULT_NUMBER_OF_COLUMNS
                }
                elements={renderCards()}
                showOverlap={showOverlap}
              />
            </>
          ) : (
            <BaseResizingCMSSlider
              {...ContentfulLivePreview.getProps({
                entryId: props.id,
                fieldId: 'content',
                locale: 'en-US',
              })}
              interval={props.interval ? props.interval * 1000 : 3000}
              numberOfColumns={
                props.numberOfColumns || DEFAULT_NUMBER_OF_COLUMNS
              }
              elements={renderCards()}
              color={props.color?.textColor as string}
            />
          )}
        </div>

        {props.button && (
          <div className="flex justify-center pt-12 pb-16">
            <ButtonCms content={props.button} />
          </div>
        )}
      </div>
    </div>
  )
}
